import React, { useEffect, ReactNode } from 'react';
import styled from 'styled-components';
import { DATA_TYPE_ERROR_MESSAGE, device } from '../../../../constants';
import { ProgressBar, Title } from '../../../../components';
import { SliderLeftArrow } from '../../../home/partials/Pofrtolio/SliderLeftArrow';
import { SliderRightArrow } from '../../../home/partials/Pofrtolio/SliderRightArrow';
import { useSwiper } from '../../../../hooks/useSwiper';

type SwiperProps<T> = {
  data: T[];
  renderItem: (item: T, index: number, length: number) => ReactNode;
};

export const FeatureSwiper = <T,>({ data, renderItem }: SwiperProps<T>) => {
  const {
    isOverflow,
    isAtStart,
    isAtEnd,
    visibleIndex,
    swiperRef,
    checkForOverflow,
    updateArrowStates,
    onTouchStart,
    onTouchMove,
    onMouseDown,
    onMouseMove,
    onMouseUp,
    onMouseLeave,
    handleNextClick,
    handlePrevClick,
    barElementClick,
  } = useSwiper(data.length);

  useEffect(() => {
    checkForOverflow();
    window.addEventListener('resize', checkForOverflow);
    return () => window.removeEventListener('resize', checkForOverflow);
  }, [data, checkForOverflow]);

  useEffect(() => {
    updateArrowStates();
  }, [data, visibleIndex, updateArrowStates]);

  return (
    <Wrapper>
      {Array.isArray(data) && data.length > 0 ? (
        <SwiperContainer>
          <FeaturesSliderHeader>
            <Title title="Features" />
            {isOverflow && (
              <ArrowContainer>
                <PrevArrowContainer>
                  <ArrowPrev onClick={handlePrevClick} disabled={isAtStart}>
                    <SliderLeftArrow />
                  </ArrowPrev>
                </PrevArrowContainer>
                <NextArrowContainer>
                  <ArrowNext onClick={handleNextClick} disabled={isAtEnd}>
                    <SliderRightArrow />
                  </ArrowNext>
                </NextArrowContainer>
              </ArrowContainer>
            )}
          </FeaturesSliderHeader>
          <ElementsContainer
            $isOverflow={isOverflow}
            ref={swiperRef}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onMouseUp}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseLeave}
          >
            <ElementsList>
              {data.map((item, idx) => (
                <SlideWrapper key={idx}>
                  {renderItem(item, idx, data.length)}
                </SlideWrapper>
              ))}
            </ElementsList>
          </ElementsContainer>
          {isOverflow && (
            <ProgressBar
              data={data}
              visibleIndex={visibleIndex}
              barElementClick={barElementClick}
            />
          )}
        </SwiperContainer>
      ) : (
        <p>${DATA_TYPE_ERROR_MESSAGE}</p>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  margin: auto;
  width: 100%;
  display: flex;
  justify-content: center;
`;
const FeaturesSliderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const SwiperContainer = styled.div`
  display: flex;
  max-width: 100%;
  flex-direction: column;
  gap: 80px;
  @media screen and (${device.md}) {
    gap: 20px;
  }
`;

const ElementsContainer = styled.div<{ $isOverflow: boolean }>`
  width: 100%;
  flex-shrink: 0;
  overflow: hidden;
  scroll-behavior: smooth;
  display: ${props => (!props.$isOverflow ? 'flex' : 'block')};
  justify-content: center;
  @media screen and (${device.sm}) {
    min-height: 0;
  }
  -moz-user-select: none;
  -ms-user-select: none;
  -webkit-user-select: none;
  user-select: none;
`;

const SlideWrapper = styled.div`
  width: 100%;
  flex-shrink: 0;
`;

const ElementsList = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  transition: 2s;
`;

const ArrowPrev = styled.div<{ disabled: boolean }>`
  width: 100%;
  height: 40px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.2s;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'all')};
`;

const ArrowNext = styled.div<{ disabled: boolean }>`
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: 0.2s;

  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'all')};
`;
const ArrowContainer = styled.div`
  display: flex;
  gap: 10px;
`;
const PrevArrowContainer = styled.div`
  width: 28px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const NextArrowContainer = styled.div`
  width: 28px;
  display: flex;
  justify-content: center;
  align-items: center;
`;
